import { useCallback, useEffect, useState } from 'react';
import { useAuthStore } from '@/stores/authStore';
import useAlertStore from '@/stores/alertStore';
import { fetchUpdateUser } from '@/services/noteGoApi/noteGoApiComponents';
import type { UserUpdate } from '@/services/noteGoApi/noteGoApiSchemas';
import { errorReporter } from '@/services/errorReportingService';
import type { UserWithZapier } from '../types';

const ZAPIER_WEBHOOK_PREFIX = 'https://hooks.zapier.com/';

export const useZapierWebhook = () => {
  const { userData, userToken, fetchAndSetUserData } = useAuthStore();
  const showAlert = useAlertStore((state) => state.showAlert);
  const zapierUser = userData as UserWithZapier | null;

  const [value, setValue] = useState('');
  const [lastSavedValue, setLastSavedValue] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const initialValue = zapierUser?.zapier_webhook_url ?? '';
    setValue(initialValue);
    setLastSavedValue(initialValue);
  }, [zapierUser?.zapier_webhook_url]);

  const isValid = value.trim() === '' || value.trim().startsWith(ZAPIER_WEBHOOK_PREFIX);

  const handleChange = useCallback((nextValue: string) => {
    setValue(nextValue);
  }, []);

  const handleSave = useCallback(async () => {
    if (!userData?.id || !userToken) {
      showAlert('Unable to save your webhook URL right now. Please try again later.', 'error');
      return;
    }

    const sanitizedValue = value.trim();

    if (sanitizedValue && !sanitizedValue.startsWith(ZAPIER_WEBHOOK_PREFIX)) {
      showAlert('Webhook URL must start with https://hooks.zapier.com/', 'error');
      return;
    }

    setIsSaving(true);

    try {
      await fetchUpdateUser({
        headers: { authorization: `Bearer ${userToken}` },
        pathParams: { userId: userData.id },
        body: {
          zapier_webhook_url: sanitizedValue || null,
        } as UserUpdate & Pick<UserWithZapier, 'zapier_webhook_url'>,
      });

      try {
        await fetchAndSetUserData({ force: true });
        setLastSavedValue(sanitizedValue);
        setValue(sanitizedValue);
        showAlert('Your Zapier webhook URL has been saved.', 'success');
      } catch (refreshError) {
        errorReporter.expected('Failed to refresh user data after updating Zapier webhook URL', refreshError, {
          feature: 'zapier',
          operation: 'refreshAfterSave',
        });
        setLastSavedValue(sanitizedValue);
        setValue(sanitizedValue);
        showAlert(
          'Your webhook URL was saved, but we could not refresh your data. Please reload the page to see the latest information.',
          'warning',
        );
      }
    } catch (error) {
      errorReporter.expected('Failed to update Zapier webhook URL', error, {
        feature: 'zapier',
        operation: 'saveWebhookUrl',
      });
      showAlert('Failed to save your webhook URL. Please try again.', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [fetchAndSetUserData, showAlert, userData?.id, userToken, value]);

  const isDirty = value.trim() !== lastSavedValue.trim();

  return {
    value,
    onChange: handleChange,
    onSave: handleSave,
    isSaving,
    isDirty,
    isValid,
  };
};
